import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { createOptionalSportFilter, resetOptionalSportFilter } from '@/helpers/sportFilter';
import { WlTransactionItem } from '@/models/wlTransaction';
import { createWlTransactionPageHeaders } from '@/pages/admin/wlTransaction/WlTransactionPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { wlTransactionService } from '@/services/wlTransactionService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import type { DataTableHeader } from '@/types/vuetify';
import { IWlTransactionFilters } from '@/types/wlTransaction';

const defaultSortBy = [{ key: 'created_at', order: 'desc' as const }];

function createWlTransactionStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<WlTransactionItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IWlTransactionFilters>({
        sportId: createOptionalSportFilter(),
        datePeriod: {
            value: 'range',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        wlTransactionDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'wlTransaction.transactionDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
        wlTransactionSearch: {
            value: null,
            title: 'wlTransaction.search',
            allowSaveState: false,
        },
        userId: {
            value: null,
            title: 'bets.player',
            allowSaveState: true,
        },
        wlTransactionTypeId: {
            value: null,
            title: 'wlTransaction.type',
            allowSaveState: true,
        },
        wlTransactionOperationId: {
            value: null,
            title: 'wlTransaction.operation',
            allowSaveState: true,
        },
        wlTransactionStatusId: {
            value: null,
            title: 'wlTransaction.status',
            allowSaveState: true,
        },
        wlTransactionDescription: {
            value: null,
            title: 'wlTransaction.description',
            allowSaveState: false,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 100,
        rowsPerPageItems: [100, 200, 500],
        sortBy: [...defaultSortBy],
    });

    // --- reset ---
    function resetFilters() {
        resetOptionalSportFilter(filters.sportId);
        filters.datePeriod.value = 'range';
        filters.wlTransactionDate.value = todayDateRangeInUserTimeZone();
        filters.wlTransactionSearch.value = null;
        filters.userId.value = null;
        filters.wlTransactionTypeId.value = null;
        filters.wlTransactionOperationId.value = null;
        filters.wlTransactionStatusId.value = null;
        filters.wlTransactionDescription.value = null;
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createWlTransactionPageHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'wlTransaction',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const filtersToSend = cloneDeep(filters);
            if (filters.wlTransactionSearch.value) {
                filtersToSend.wlTransactionDate = { ...filtersToSend.wlTransactionDate, value: null };
                filtersToSend.datePeriod = { ...filtersToSend.datePeriod, value: null };
            }

            const params: IListParams<IWlTransactionFilters> = {
                filters: filtersToSend,
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AuditTable'],
            };

            const response = await wlTransactionService.list(params);
            items.value = response.data.rows.map((row) => new WlTransactionItem(row));
            total.value = response.data.total;
        } catch (e) {
            if (isRequestCanceled(e)) return;
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        hasRefresh: false,
    };
}

export function useWlTransactionStore(storeId: string = 'wlTransaction') {
    const useStore = defineStore(storeId, () => createWlTransactionStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'wlTransaction'),
    });
    return useStore();
}
